import { LIST_EXPORT_SCHEMA_V1, PLAN_PACKAGE_SCHEMA_V2 } from "@shared/types";
import { downloadJsonFile, sanitizeFilename } from "@/lib/download";
/**
 * Map a plan's content to list-only export sections. Includes ONLY structure
 * (sections, subsections, items + quantity/fulfillmentMode). Deliberately omits
 * all people/assignment/completion state: checked, assignments, assignmentType,
 * assignedTo*, notes, participants, active users, revisions, plan id/token.
 */
function toExportSections(content) {
    return content.sections.map((section) => {
        const subsections = section.subsections.map((sub) => {
            const items = sub.items.map((item) => {
                const out = { text: item.text };
                if (item.quantity)
                    out.quantity = item.quantity;
                if (item.fulfillmentMode && item.fulfillmentMode !== "normal") {
                    out.fulfillmentMode = item.fulfillmentMode;
                }
                return out;
            });
            const subOut = { items };
            if (sub.title)
                subOut.title = sub.title;
            return subOut;
        });
        const sectionOut = { title: section.title, subsections };
        if (section.icon)
            sectionOut.icon = section.icon;
        if (section.sectionKind && section.sectionKind !== "normal") {
            sectionOut.sectionKind = section.sectionKind;
        }
        return sectionOut;
    });
}
/**
 * Build a list-only export of a plan (v1 schema, kept for backward
 * compatibility). See {@link toExportSections} for what is/isn't included.
 */
export function buildListExport(content, title) {
    return {
        schema: LIST_EXPORT_SCHEMA_V1,
        title,
        exportedAt: Math.floor(Date.now() / 1000),
        sections: toExportSections(content),
    };
}
/**
 * Build a v2 plan package export. `kind` distinguishes a plain list from a
 * reusable template; the section structure is identical (list-only) but the
 * envelope metadata differs so importers can label and treat them accordingly.
 */
export function buildPlanPackageExport(content, title, kind) {
    const pkg = {
        schema: PLAN_PACKAGE_SCHEMA_V2,
        version: 2,
        app: "nodeone",
        kind,
        locale: "he",
        title,
        exportedAt: new Date().toISOString(),
        sections: toExportSections(content),
    };
    if (kind === "template") {
        pkg.description = "תבנית לשימוש חוזר כבסיס לתכנון אחר (מבנה בלבד, ללא אנשים או שיוכים).";
    }
    return pkg;
}
/**
 * Build a valid v2 template package with Hebrew sample content, suitable for
 * downloading as a "starter" file users can edit and re-import.
 */
export function buildSampleTemplate() {
    return {
        schema: PLAN_PACKAGE_SCHEMA_V2,
        version: 2,
        app: "nodeone",
        kind: "template",
        locale: "he",
        title: "תבנית לדוגמה",
        description: "קובץ תבנית לדוגמה — ערכו אותו וייבאו אותו לתכנון שלכם.",
        exportedAt: new Date().toISOString(),
        sections: [
            {
                title: "אוכל",
                icon: "🍽️",
                subsections: [
                    {
                        title: "מנות עיקריות",
                        items: [
                            { text: "סלט ירקות", quantity: "2 קערות" },
                            { text: "פסטה" },
                        ],
                    },
                ],
            },
            {
                title: "קניות",
                icon: "🛒",
                sectionKind: "shopping",
                subsections: [
                    {
                        items: [
                            { text: "מים", quantity: "6 בקבוקים", fulfillmentMode: "buy_there" },
                            { text: "פחמים", fulfillmentMode: "buy_there" },
                        ],
                    },
                ],
            },
        ],
    };
}
/** Trigger a browser download of the sample v2 template JSON file. */
export function downloadSampleTemplate() {
    downloadJsonFile(buildSampleTemplate(), "nodeone-תבנית-לדוגמה.json");
}
/** Build a Hebrew-friendly download filename: "<kind>-<title>-<date>.json". */
function packageFilename(title, kind) {
    const date = new Date().toISOString().slice(0, 10);
    const prefix = kind === "template" ? "תבנית" : "רשימה";
    const safeTitle = sanitizeFilename(title, "nodeone");
    return `${prefix}-${safeTitle}-${date}.json`;
}
/** Trigger a browser download of the v1 list export as a UTF-8 JSON file. */
export function downloadListExport(content, title) {
    const data = buildListExport(content, title);
    downloadJsonFile(data, packageFilename(title, "list"));
}
/**
 * Trigger a browser download of a v2 plan package (list or template) as a
 * UTF-8 JSON file with a Hebrew-friendly filename.
 */
export function downloadPlanPackageExport(content, title, kind) {
    const data = buildPlanPackageExport(content, title, kind);
    downloadJsonFile(data, packageFilename(title, kind));
}
